/**
 * «PROVENIENZA» — where a page's numbers come from, in words (production visual audit, owner
 * decision 2026-10-10, A2).
 *
 * The line used to print what the caller handed it, verbatim: API paths
 * (`inseason/runs/…/stock-summary`), digests and run ids. A planner cannot read those; support
 * can. So the line is split in two:
 *
 *   - what a person reads stays on the line — «Dati dell’esecuzione CO · 2026-W41 · Negozi
 *     diretti · eseguita il 9 ott 2026, al 9 ott 2026», the source's name, a date;
 *   - what only support reads — the paths, the codes, the identifiers — moves behind a native
 *     «Dettagli tecnici» disclosure, collapsed, with the run's «id» (tooltip and copy) beside the
 *     sentence.
 *
 * **The callers do not change.** `parts` is still a list of strings; each is sorted here:
 *
 *   - a run the run directory lists → the run, in words;
 *   - a path (`a/b`), a snake_case or dotted code, a digest or any other identifier → technical;
 *   - an ISO date → a date, in the planner's format;
 *   - anything else — a source label, a formatted date — stays as given.
 *
 * Nothing is filled in: a part the caller does not pass is a part not shown. The exports carry
 * their own provenance and do not read this line.
 */

import { useTranslation } from 'react-i18next';

import { useApiCollection } from '../../api';
import { inseasonListRuns } from '../../api/generated';
import { formatDate, formatDateTime } from '../../lib/format';
import { FOCUS_RING } from '../../primitives';
import { findRun, IdTag, runSlotFallback, runWords, useHasQueryClient, type RunNamingRow } from '../run-words';

/** An identifier as the platform mints them: hexadecimal (a digest, a run), possibly dashed (a UUID). */
const IDENTIFIER = /^[0-9a-f]{6,}(?:-[0-9a-f]+)*$/i;
/** `a/b`, `inseason/runs/x/stock-summary`: a path or an endpoint, with no space in it. */
const PATH = /^[^\s/]+(?:\/[^\s/]+)+$/;
/** `inseason.distribute`, `model_v1_2`: a code in lower case with a dot or an underscore in it. */
const CODE = /^[a-z][a-z0-9]*(?:[._][a-z0-9]+)+$/;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const ISO_DATE_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?$/;

export type ProvenancePartKind = 'run' | 'technical' | 'date' | 'dateTime' | 'text';

/** What one part of the line is. Pure. */
export function provenancePartKind(part: string, knownRun: (id: string) => boolean = () => false): ProvenancePartKind {
  const value = part.trim();
  if (value === '') return 'text';
  if (knownRun(value)) return 'run';
  if (IDENTIFIER.test(value)) return 'technical';
  if (ISO_DATE.test(value)) return 'date';
  if (ISO_DATE_TIME.test(value)) return 'dateTime';
  if (PATH.test(value) || CODE.test(value)) return 'technical';
  return 'text';
}

/** The line, sorted: the runs it names, what only support reads, and what stays on the line. */
export interface ProvenanceSplit {
  readonly runs: readonly string[];
  readonly technical: readonly string[];
  /** In the caller's order; a date is `{ date }`/`{ dateTime }` until it is formatted. */
  readonly visible: readonly (string | { readonly date: string } | { readonly dateTime: string })[];
}

export function splitProvenance(parts: readonly string[], knownRun: (id: string) => boolean = () => false): ProvenanceSplit {
  const runs: string[] = [];
  const technical: string[] = [];
  const visible: (string | { readonly date: string } | { readonly dateTime: string })[] = [];
  for (const part of parts) {
    switch (provenancePartKind(part, knownRun)) {
      case 'run': runs.push(part.trim()); break;
      case 'technical': technical.push(part.trim()); break;
      case 'date': visible.push({ date: part.trim() }); break;
      case 'dateTime': visible.push({ dateTime: part.trim() }); break;
      default: visible.push(part);
    }
  }
  return { runs, technical, visible };
}

/** The run directory, as far as it has been read — `null` where it cannot be read at all. */
interface RunLookup {
  readonly rows: readonly RunNamingRow[] | undefined;
}

function ProvenanceView({ parts, lookup }: { readonly parts: readonly string[]; readonly lookup: RunLookup | null }): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const split = splitProvenance(parts, (id) => findRun(lookup?.rows, id) !== undefined);

  const runName = (id: string): string => {
    const row = findRun(lookup?.rows, id);
    return row === undefined ? runSlotFallback(t, language) : runWords(row, t, language);
  };
  // The first date joins the run's sentence («… , al 9 ott 2026»); the rest stay where they were.
  const firstDate = split.visible.findIndex((part) => typeof part !== 'string' && 'date' in part);
  const [firstRun, ...otherRuns] = split.runs;

  const shown: React.ReactNode[] = [];
  if (firstRun !== undefined) {
    const date = firstDate < 0 ? undefined : split.visible[firstDate];
    const day = date !== undefined && typeof date !== 'string' && 'date' in date ? formatDate(date.date, language, 'UTC') : null;
    shown.push(
      <span key="run" className="inline-flex flex-wrap items-baseline gap-1">
        <span>{day === null ? t('template.provenance.run', { run: runName(firstRun) }) : t('template.provenance.runAt', { run: runName(firstRun), date: day })}</span>
        <IdTag id={firstRun} kind={t('idTag.kindRun')} />
      </span>,
    );
  }
  for (const id of otherRuns) {
    shown.push(
      <span key={`run-${id}`} className="inline-flex flex-wrap items-baseline gap-1">
        <span>{t('template.provenance.otherRun', { run: runName(id) })}</span>
        <IdTag id={id} kind={t('idTag.kindRun')} />
      </span>,
    );
  }
  split.visible.forEach((part, index) => {
    if (typeof part === 'string') {
      shown.push(<span key={`${index}-${part}`}>{part}</span>);
    } else if ('date' in part) {
      if (index === firstDate && firstRun !== undefined) return;
      shown.push(<span key={`${index}-${part.date}`}>{formatDate(part.date, language, 'UTC')}</span>);
    } else {
      shown.push(<span key={`${index}-${part.dateTime}`}>{formatDateTime(part.dateTime, language, 'UTC')}</span>);
    }
  });

  // Support's half: every path, code and identifier, as the caller gave it.
  const details = [...split.technical, ...split.runs];
  return (
    <div data-template-provenance="" data-tour="provenance" className="text-ink-secondary flex flex-wrap items-baseline gap-x-2 gap-y-1 text-[11px] leading-4">
      <span className="text-ink-primary text-[10px] font-bold uppercase tracking-[0.08em]">{t('template.provenance')}</span>
      <span className="font-numeric">
        {shown.map((node, index) => (
          <span key={index}>
            {index > 0 && <span aria-hidden className="px-1">·</span>}
            {node}
          </span>
        ))}
      </span>
      {details.length === 0 ? null : (
        <details data-provenance-details="">
          <summary className={`text-ink-secondary cursor-pointer underline ${FOCUS_RING}`}>{t('template.provenance.technical')}</summary>
          <span className="font-numeric mt-1 block break-all">
            {details.map((detail, index) => (
              <span key={`${index}-${detail}`}>
                {index > 0 && <span aria-hidden className="px-1">·</span>}
                {detail}
              </span>
            ))}
          </span>
        </details>
      )}
    </div>
  );
}

/** With a query client: the run directory (a warm cache entry on a run-scoped page) names the runs. */
function ProvenanceWithRuns({ parts }: { readonly parts: readonly string[] }): React.JSX.Element {
  const names = parts.some((part) => IDENTIFIER.test(part.trim()));
  const directory = useApiCollection(inseasonListRuns, { query: {} }, { enabled: names });
  const lookup: RunLookup = { rows: directory.data };
  return <ProvenanceView parts={parts} lookup={lookup} />;
}

export function ProvenanceLine({ parts }: { readonly parts: readonly string[] }): React.JSX.Element | null {
  const hasClient = useHasQueryClient();
  if (parts.length === 0) return null;
  return hasClient ? <ProvenanceWithRuns parts={parts} /> : <ProvenanceView parts={parts} lookup={null} />;
}
